import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ToggleGroup, ToggleGroupItem, cn } from "@nexus/ui";
import type { NorthStarKpi } from "@nexus/contracts";
import { toast } from "sonner";

import { kpiCatalogKeys, tenantDefaultNorthStarQueryOptions } from "@/lib/queries";
import { tenantSettingsKeys } from "@/lib/queries/tenant-settings.queries";
import { getUserFacingApiMessage } from "@/lib/api-client";
import { updateTenantDefaultNorthStar } from "@/features/identity-hub/api/tenant-kpis.api";
import { SettingsField } from "./shared/SettingsRow";
import { SEGMENT_ITEM_CLASS, SEGMENT_TRACK_CLASS } from "./shared/segment-toggle-classes";

const USERS_CHOICE = "none";

const NORTH_STAR_OPTIONS = [
  { value: USERS_CHOICE, labelKey: "iam.orgDrawer.settings.defaultNorthStarShortNone", labelDefault: "User's" },
  { value: "roas", labelKey: "iam.orgDrawer.settings.defaultNorthStarRoas", labelDefault: "ROAS" },
  { value: "cpa", labelKey: "iam.orgDrawer.settings.defaultNorthStarCpa", labelDefault: "CPA" },
  { value: "custom", labelKey: "iam.orgDrawer.settings.defaultNorthStarShortCustom", labelDefault: "Custom" },
] as const;

interface OrgDefaultNorthStarControlProps {
  tenantId: string;
  disabled: boolean;
}

/** Tenant Default NorthStar for new users. Persists on change, like the rest of Org KPIs. */
export function OrgDefaultNorthStarControl({ tenantId, disabled }: OrgDefaultNorthStarControlProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery(tenantDefaultNorthStarQueryOptions(tenantId));

  const saveMut = useMutation({
    mutationFn: (defaultNorthStar: NorthStarKpi | null) =>
      updateTenantDefaultNorthStar(tenantId, { defaultNorthStar }),
    onSuccess: (saved) => {
      queryClient.setQueryData(kpiCatalogKeys.tenantDefaultNorthStar(tenantId), saved);
      void queryClient.invalidateQueries({ queryKey: tenantSettingsKeys.settings(tenantId) });
    },
    onError: (error) => toast.error(getUserFacingApiMessage(error)),
  });

  const hasData = query.data !== undefined;
  const selected = saveMut.isPending ? saveMut.variables : (query.data?.defaultNorthStar ?? null);
  const label = t("iam.orgDrawer.settings.defaultNorthStarLabel", "Default NorthStar KPI");

  return (
    <SettingsField
      label={label}
      hint={t("iam.orgDrawer.settings.defaultNorthStarSegmentHint", "User's = each person keeps their own preference")}
      error={
        query.isError
          ? t(
              "iam.orgDrawer.settings.defaultNorthStarLoadError",
              "Couldn't load the default NorthStar. Reload to try again.",
            )
          : undefined
      }
    >
      {query.isLoading ? (
        <div className="h-9 max-w-[360px] rounded-md border border-border bg-muted/40 animate-pulse" />
      ) : (
        <ToggleGroup
          type="single"
          variant="default"
          data-testid="org-settings-default-north-star"
          aria-label={label}
          value={hasData ? (selected ?? USERS_CHOICE) : ""}
          onValueChange={(next) => {
            if (!next) return;
            saveMut.mutate(next === USERS_CHOICE ? null : (next as NorthStarKpi));
          }}
          disabled={disabled || !hasData || saveMut.isPending}
          className={cn(SEGMENT_TRACK_CLASS, "h-9 max-w-[360px] grid-cols-4 gap-0.5 p-0.5")}
        >
          {NORTH_STAR_OPTIONS.map((option) => (
            <ToggleGroupItem
              key={option.value}
              value={option.value}
              className={cn(SEGMENT_ITEM_CLASS, "h-full min-w-0 px-1 text-[11px] font-medium")}
            >
              {t(option.labelKey, option.labelDefault)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      )}
    </SettingsField>
  );
}
